import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';

import { Button } from 'UIPackage/MainModule/components/Button';
import { Dropdown } from 'UIPackage/MainModule/components/Dropdown';
import { IDropdownItem } from 'UIPackage/MainModule/interfaces/IDropdownItem';
import { DropdownItem } from 'UIPackage/MainModule/components/DropdownItem';
import { ROUTE_ADMIN_USERS, ROUTE_INFO, ROUTE_SMEV_MONITORING } from 'CorePackage/MainModule/routes/routes';
import { IUserInfo } from 'APIPackage/AuthModule/interfaces/IUserInfo';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { getKnmFormRef } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { getUserInfo, getUserRoleIsAdmin, getUserProfileIsTest } from 'AuthPackage/MainModule/selectors/authSelectors';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { authLogout } from 'AuthPackage/MainModule/actions/authActions';
import { getDecisionFormRef } from 'DecisionPackage/MainModule/selectors/decisionSelectors';

import styles from './Header.module.scss';
import { setIsHeaderStickedAction } from '../actions/headerActions';
import { getIsSticked } from '../reducers/headerReducer';

interface IHeaderDropdownProps {
  isAdmin: boolean;
  isKnm: boolean;
  isDecision: boolean;
  userInfo?: IUserInfo;
  isSticked: boolean;
  isTestUser: boolean;
}

const HeaderDropdownWithoutStore = ({
  isAdmin,
  isKnm,
  userInfo,
  isSticked,
  isDecision,
  isTestUser,
}: IHeaderDropdownProps) => {
  const history = useHistory();
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const username = useMemo(() => userInfo?.userAccount.login ?? '', [userInfo]);
  const firstName = useMemo(() => userInfo?.userProfile.nameFirst ?? '', [userInfo]);
  const lastName = useMemo(() => userInfo?.userProfile.nameLast ?? '', [userInfo]);
  const middleName = useMemo(() => userInfo?.userProfile.nameMiddle ?? '', [userInfo]);
  const userFullName = useMemo(() => [firstName, middleName, lastName].filter(Boolean).join(' '), [
    firstName,
    middleName,
    lastName,
  ]);

  const navigateToProfile = useCallback(() => {
    history.push(isAdmin ? ROUTE_ADMIN_USERS : ROUTE_INFO);
  }, [history, isAdmin]);

  const navigateToSmevMonitoring = useCallback(() => {
    history.push(ROUTE_SMEV_MONITORING);
  }, [history]);

  const updateHeaderStickedState = useCallback(() => {
    const headerElement = document.querySelector('header');

    if (headerElement) {
      const windowOffset = window.scrollY;

      headerElement.setAttribute('data-scroll', windowOffset > headerElement.offsetHeight ? 'true' : 'false');
      if (windowOffset > headerElement.offsetHeight) {
        headerElement.classList.add(styles.HeaderSticked);
        setIsDropdownVisible(false);
        dispatch(setIsHeaderStickedAction(true));
      } else {
        headerElement.classList.remove(styles.HeaderSticked);
        dispatch(setIsHeaderStickedAction(false));
      }
    }
  }, [dispatch]);

  useEffect(() => {
    updateHeaderStickedState();
  }, [updateHeaderStickedState]);

  useEffect(() => {
    window.addEventListener('scroll', updateHeaderStickedState);

    return () => window.removeEventListener('scroll', updateHeaderStickedState);
  });

  const toProfile: IDropdownItem[] = useMemo(
    () => [
      {
        id: 'profile',
        content: (
          <DropdownItem onClick={navigateToProfile} id="personalAccountButton">
            Личный кабинет
          </DropdownItem>
        ),
      },
    ],
    [navigateToProfile],
  );

  const toSmevMonitoring: IDropdownItem[] = useMemo(
    () => [
      {
        id: 'monitoring',
        content: <DropdownItem onClick={navigateToSmevMonitoring}>Мониторинг сообщений СМЭВ</DropdownItem>,
      },
    ],
    [navigateToSmevMonitoring],
  );

  const handleLogoutClick = useCallback(() => {
    dispatch(authLogout());
  }, [dispatch]);

  const toLogout: IDropdownItem[] = useMemo(
    () => [
      {
        id: 'divider-1',
        isDivider: true,
      },
      {
        id: 'logout',
        content: (
          <DropdownItem onClick={handleLogoutClick} id="logoutButton">
            Выйти
          </DropdownItem>
        ),
      },
    ],
    [handleLogoutClick],
  );

  const dropdownItems = useMemo(() => {
    let res: IDropdownItem[] = [];

    res = [...res, ...toProfile];
    if (isAdmin) {
      res = [...res, ...toSmevMonitoring];
    }
    res = [...res, ...toLogout];

    return res;
  }, [isAdmin, toLogout, toSmevMonitoring, toProfile]);

  const handleDropdownVisibleChange = useCallback((visible: boolean) => setIsDropdownVisible(visible), []);
  const buttonAttributes = {
    ...(isDropdownVisible && { 'data-active': true }),
    ...((isKnm || isDecision) && isSticked && { 'data-hidden': true }),
  };

  return (
    <Dropdown items={dropdownItems} onVisibleChange={handleDropdownVisibleChange} isTopMenu={true} hideByScroll={true}>
      {isTestUser && (
        <div className={styles.TestUserWrapper} {...buttonAttributes}>
          ТЕСТ
        </div>
      )}
      <Button
        className={styles.DropdownButton}
        text={userFullName || username}
        variant="link"
        id="userMenuButton"
        {...buttonAttributes}
      />
    </Dropdown>
  );
};

const mapStateToProps = (state: IRootState) => ({
  isKnm: !!getKnmFormRef(state),
  isDecision: !!getDecisionFormRef(state),
  userInfo: getUserInfo(state),
  isAdmin: !!getUserRoleIsAdmin(state),
  isSticked: getIsSticked(state),
  isTestUser: getUserProfileIsTest(state),
});

export const HeaderDropdown = connect(mapStateToProps)(HeaderDropdownWithoutStore);
